import React from 'react';
import { useSelector } from 'react-redux';
import { Hidden, Visible } from 'react-grid-system';
import Icon from '@Components/Icon/Icon';
import Divider from '@Components/Divider/Divider';
import Toggle from '@Components/controls/Toggle/Toggle';
import { Caption } from '@Components/Typography/Typography';
import BusyIndicator from '@Components/BusyIndicator/BusyIndicator';
import { PrimaryMediumButtonFixed } from '@Components/controls/Button/Button';
import ConditionalRender from '@Components/ConditionalRender/ConditionalRender';
import useTranslate from '@Utils/hooks/useTranslate';
import { GET_ADDRESS_BOOK_BI } from '@Utils/hooks/useAddressBook';
import { IAddress } from '@ApiModels/commerceProfile';
import { SelectedAddressType } from './../ShippingMethod';
import AddressBookDetails from '../../AddressBook/AddressBookDetails';
import {
	AddressBookElement,
	AddressTitle,
	Horizontal,
	Separator,
	ToggleWrapper,
	Underline,
	ToggleText,
} from './../ShippingMethod.styled';
import { IconWrapper } from '@Components/Card/cards/TermsAndConditions/TermsAndConditions.styled';

interface IAddressBookItem {
	addressBook: IAddress[];
	addressType: SelectedAddressType;
	editAddress: (address: IAddress, isIncorrectArea?: boolean) => void;
	data: IAddress[];
	filter: string | undefined;
	getTitle: (address: IAddress) => string;
	isPrepaidPurchase: boolean | undefined;
	removeAddress: (nickName: string) => void;
	setToggleState: (address: IAddress, isChecked: boolean) => void;
}

const AddressBookList = ({
	addressBook,
	addressType,
	editAddress,
	data,
	filter,
	getTitle,
	isPrepaidPurchase,
	removeAddress,
	setToggleState,
}: IAddressBookItem): JSX.Element => {
	const { translate } = useTranslate();
	const activeAddressBook = useSelector((state: types.redux.IState) => state.addressBook.activeAddressBook);
	const newAddressBook = useSelector((state: types.redux.IState) => state.addressBook.newAddressBook);

	const addressList = data.map((address, index) => (
		<BusyIndicator listener={GET_ADDRESS_BOOK_BI} key={index} skeleton="orderTracking">
			<AddressBookElement key={index}>
				<Horizontal>
					<IconWrapper>
						<Icon name="location" fill="black54" width={32} height={32} />
					</IconWrapper>
					<AddressTitle>{getTitle(address)}</AddressTitle>
					{addressBook.length > 1 && !filter && (
						<ToggleWrapper>
							<ConditionalRender
								show={!isPrepaidPurchase}
								onTrue={() => (
									<ToggleText>
										<Caption color="black54">
											{activeAddressBook === address.addressId
												? translate('secure-checkout.payment-tokens.selected')
												: ''}
										</Caption>
									</ToggleText>
								)}
							/>
							<Toggle
								active={address.addressId === activeAddressBook}
								onChange={() => setToggleState(address, !(address.addressId === activeAddressBook))}
							/>
						</ToggleWrapper>
					)}
				</Horizontal>
				<ConditionalRender
					show={
						activeAddressBook === address.addressId ||
						(addressType === SelectedAddressType.DIFFERENT && newAddressBook === address.addressId)
					}
					onTrue={() => (
						<>
							<Divider withoutLine marginTop={24} />
							<AddressBookDetails
								address={address}
								removeAddress={(nickName) => {
									removeAddress(nickName ?? '');
								}}
								editAddress={() => {
									editAddress(address);
								}}
							/>
						</>
					)}
				/>
				<Divider withoutLine marginBottom={26} />
				<Horizontal>
					<ConditionalRender
						show={!!isPrepaidPurchase}
						onTrue={() => (
							<>
								<PrimaryMediumButtonFixed
									uniqueId="shipping-method.address-book-list.edit-address"
									onClick={() => {
										editAddress(address);
									}}
								>
									{translate('address.edit.address')}
								</PrimaryMediumButtonFixed>
								<Visible xs>
									<Divider withoutLine marginBottom={10} />
								</Visible>
								<Hidden xs>
									<Separator />
								</Hidden>
								<PrimaryMediumButtonFixed
									uniqueId="shipping-method.edit-address"
									onClick={() => {
										removeAddress(address.nickName ?? '');
									}}
								>
									{translate('modal.delete-payment-token.delete')}
								</PrimaryMediumButtonFixed>
							</>
						)}
						onFalse={() => (
							<>
								<Separator />
								<Separator />
								<Separator />
								<Underline color="primary" onClick={() => editAddress(address)}>
									{translate('address.edit.address')}
								</Underline>
								<Separator />
								<Underline color="primary" onClick={() => removeAddress(address.nickName ?? '')}>
									{translate('modal.delete-payment-token.delete')}
								</Underline>
							</>
						)}
					/>
				</Horizontal>
			</AddressBookElement>
		</BusyIndicator>
	));

	return <>{addressList}</>;
};

export default AddressBookList;
